import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

/**
 * Punto de entrada real cuando Commercial Operator se abre embebido dentro
 * de GoHighLevel (Custom Menu Link / iFrame) — el destino del menú que
 * Abel ve después de entrar a app.comercialoperator.com con su correo y
 * contraseña de White Label. Reemplaza /socio-fundador?t= para ese caso:
 * aquí no hay token en la URL ni segunda pantalla de "bienvenida antes de
 * entrar" — GHL ya autenticó a Abel, este componente solo demuestra esa
 * identidad ante nuestro backend.
 *
 * Flujo (ver backend/app/routers/ghl_sso.py y
 * FASE4_AUDITORIA_SSO_MARKETPLACE_GHL.md en el repo LION AGENCIA IA):
 * 1. Pide el contexto de usuario a la ventana padre (el dashboard de GHL)
 *    vía postMessage — esto solo funciona dentro de un iframe real de GHL,
 *    nunca en una pestaña suelta.
 * 2. Reenvía el payload cifrado, sin tocarlo, a POST /api/ghl/sso — el
 *    frontend nunca ve el Shared Secret ni descifra nada.
 * 3. Si el backend confirma la Location autorizada, ya quedó la cookie de
 *    sesión puesta — navega a /bienvenida como cualquier primer ingreso.
 */
const REQUEST_TIMEOUT_MS = 8000;

export default function GHLEntry() {
  const navigate = useNavigate();
  const [status, setStatus] = useState("waiting"); // waiting | verifying | error
  const [errorDetail, setErrorDetail] = useState("");

  useEffect(() => {
    let settled = false;

    function handleMessage(event) {
      if (settled) return;
      const data = event?.data;
      if (!data || data.message !== "REQUEST_USER_DATA_RESPONSE") {
        return;
      }

      const encryptedData = data.payload ?? data.data ?? data.encryptedData;
      if (!encryptedData) {
        return;
      }

      settled = true;
      window.removeEventListener("message", handleMessage);
      setStatus("verifying");

      fetch("/api/ghl/sso", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ encryptedData }),
      })
        .then(async (res) => {
          if (!res.ok) {
            const body = await res.json().catch(() => ({}));
            throw new Error(body.detail || `Error ${res.status}`);
          }
          return res.json();
        })
        .then((body) => {
          navigate(body.redirect || "/bienvenida", { replace: true });
        })
        .catch((err) => {
          setStatus("error");
          setErrorDetail(err.message || "No se pudo verificar la sesión");
        });
    }

    window.addEventListener("message", handleMessage);
    window.parent.postMessage({ message: "REQUEST_USER_DATA" }, "*");

    const timeout = setTimeout(() => {
      if (settled) return;
      settled = true;
      window.removeEventListener("message", handleMessage);
      setStatus("error");
      setErrorDetail(
        "No recibimos respuesta de GoHighLevel. Esta pantalla solo funciona dentro del menú Commercial Operator, no en una pestaña abierta directamente."
      );
    }, REQUEST_TIMEOUT_MS);

    return () => {
      window.removeEventListener("message", handleMessage);
      clearTimeout(timeout);
    };
  }, [navigate]);

  return (
    <div className="min-h-screen bg-[#0d1321] text-zinc-100 flex items-center justify-center px-5 sm:px-8">
      <div className="max-w-md text-center">
        <div className="text-[11px] text-amber-500 font-mono uppercase tracking-[0.35em] mb-6">
          Commercial Operator
        </div>
        {status !== "error" ? (
          <p className="text-zinc-400 text-sm leading-relaxed">
            {status === "waiting" ? "Verificando tu sesión de GoHighLevel..." : "Confirmando acceso..."}
          </p>
        ) : (
          <>
            <p className="text-zinc-300 text-sm leading-relaxed">
              No pudimos verificar tu acceso.
            </p>
            <p className="text-zinc-600 text-xs mt-2 leading-relaxed">{errorDetail}</p>
          </>
        )}
      </div>
    </div>
  );
}
